import { Skeleton, Stack } from '@mui/material'
import flatMap from 'lodash/flatMap'
import { useCallback, useMemo } from 'react'
import { ScrollTag } from 'utoniq-ui'

import {
  EventHeldStatus,
  Maybe,
  MyPreSaleEventConnectionFragment,
  MyTicketsQuery,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { EventTicketCard } from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'
import { setItemSku } from '@/core/sku'

export * from './fragments'

export type State = {
  loading: boolean
  fetching: boolean
  hasNextPage: boolean
  filter: EventHeldStatus
  user: MyTicketsQuery['user']
  events: Maybe<MyPreSaleEventConnectionFragment>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
  onFilter: (filterType: EventHeldStatus) => void
}

export const MyTicketsTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onFilter,
}) => {
  const { t } = useTranslation('myTickets')
  const { routerNames } = useRouter()

  const events = useMemo(() => {
    if (!state.events?.edges) {
      return undefined
    }
    return flatMap(
      state.events?.edges,
      ({ node }) =>
        node.myPreSaleItems?.map(myPreSaleItem => ({
          event: node,
          ...myPreSaleItem,
        })) ?? []
    )
  }, [state.events?.edges])

  return (
    <BaseLayout
      navigation={{
        type: 'global',
        props: {
          hasManagementTask: !!state.user?.hasManagementTask,
          unreadNotificationCount: state.user?.unreadNotificationCount,
          hasSettingTask: !!state.user?.isMembershipPersonalInformationRequired,
        },
      }}
      margin
      header={{
        back: true,
        title: t('title'),
      }}
    >
      <Head title={routerNames.MyTickets} />
      <FilterMenu filter={state.filter} onFilter={onFilter} />
      <ScrollFetcher
        nodes={events?.map(({ event, item, skuId }, index) => (
          <EventTicketCard
            key={index}
            event={event}
            item={setItemSku(item as never, skuId)}
            skuId={skuId}
          />
        ))}
        loading={state.loading || state.fetching}
        hasNextPage={state.hasNextPage}
        onFetchMore={onFetchMore}
        componentProps={{ spacing: 4, pt: 2, pb: 6 }}
        emptyMessage={t('emptyEventTicketMessage')}
        loadingComponent={
          <Stack spacing={4} flexDirection="column" sx={{ pt: 2, pb: 7 }}>
            {Array.from(Array(3).keys()).map(key => (
              <Skeleton
                key={key}
                variant="rectangular"
                sx={{
                  width: '100%',
                  height: 216,
                  borderRadius: 2,
                }}
              />
            ))}
          </Stack>
        }
      />
    </BaseLayout>
  )
}

const FilterMenu = ({
  filter,
  onFilter,
}: {
  filter: EventHeldStatus
  onFilter: (filterType: EventHeldStatus) => void
}) => {
  const { t } = useTranslation('myTickets')

  const handleSelect = useCallback(
    (value: EventHeldStatus) => {
      onFilter(value)
    },
    [onFilter]
  )

  return (
    <ScrollTag
      value={filter}
      onClick={handleSelect}
      tags={[
        { value: EventHeldStatus.All, label: t('all', 'すべて') },
        { value: EventHeldStatus.Ongoing, label: t('clear', '開催中') },
        {
          value: EventHeldStatus.Upcoming,
          label: t('upcoming', '開催予定'),
        },
        {
          value: EventHeldStatus.Finished,
          label: t('alreadyHeld', '開催済'),
        },
      ]}
    />
  )
}
